import { useState, useEffect, startTransition } from 'react';

/**
 * Returns `true` once the component has mounted on the client.
 * Use this to delay rendering client-only UI until after hydration.
 *
 * This hook ensures that server-rendered HTML matches the initial client render,
 * preventing React 18 hydration mismatches.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const hasMounted = useHasMounted();
 *
 *   if (!hasMounted) {
 *     // Render what the server renders (or null)
 *     return <div>Loading...</div>;
 *   }
 *
 *   // This logic only runs on the client after hydration
 *   return window.innerWidth > 600 ? <div>Desktop</div> : <div>Mobile</div>;
 * };
 * ```
 */
export const useHasMounted = (): boolean => {
  const [hasMounted, setHasMounted] = useState(false);

  useEffect(() => {
    startTransition(() => {
      setHasMounted(true);
    });
  }, []);

  return hasMounted;
};
